import { useState, useEffect } from "react";
import { Check, Loader2 } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { TrialCalendarOptions } from "../components/TrialCalendarOptions";
import { useSiteTexts } from "../lib/useSiteTexts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Patch 3 — Success landing after Stripe Checkout for paid trial bookings.
// Stripe redirects here with ?session_id=cs_test_... The webhook may take a
// few seconds to fire; we poll with exponential backoff until either the
// appointment is confirmed or a refund is recorded.
//
// Patch A.1 — Add-to-Calendar options are now provided by the shared
// TrialCalendarOptions component. The previous inline `buildIcsFile`
// implementation has been removed to avoid duplication with the free
// direct-booking success screen.

export default function TrialBookingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id") || "";
  const [state, setState] = useState({ status: "polling", attempts: 0 });
  const [details, setDetails] = useState(null);
  const t = useSiteTexts();

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", message: "Missing session id." });
      return;
    }
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const r = await fetch(`${API}/booking/trial-direct/session/${sessionId}`);
        const d = await r.json();
        if (cancelled) return;
        if (d.status === "confirmed") {
          setDetails(d);
          setState({ status: "confirmed" });
          return;
        }
        if (d.status === "refunded") {
          setState({ status: "refunded", reason: d.reason, amount: d.amount });
          return;
        }
      } catch {
        // network hiccup, keep polling
      }
      if (attempts >= 8) {
        setState({ status: "timeout" });
        return;
      }
      const delay = Math.min(1500 * Math.pow(1.5, attempts - 1), 8000);
      setTimeout(poll, delay);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  const containerCls = "min-h-screen bg-[#FAF8F0] flex items-center justify-center px-4";
  const cardCls = "text-center max-w-md w-full";
  const headingCls = "text-2xl sm:text-3xl tracking-[0.08em] text-[#282828] mb-3";
  const headingStyle = { fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 };

  if (state.status === "polling") {
    return (
      <div className={containerCls} data-testid="success-polling">
        <div className={cardCls}>
          <Loader2 className="w-8 h-8 mx-auto mb-6 animate-spin text-[#9A7B4F]" />
          <h1 className={headingCls} style={headingStyle}>{t("trial_success_polling_heading", "Confirming your trial")}</h1>
          <p className="text-[13px] text-[#676664]/70 leading-relaxed">
            {t("trial_success_polling_subheading", "Payment received. We're just finalising your appointment — this usually takes a few seconds.")}
          </p>
        </div>
      </div>
    );
  }

  if (state.status === "refunded") {
    return (
      <div className={containerCls} data-testid="success-refunded">
        <div className={cardCls}>
          <h1 className={headingCls} style={headingStyle}>{t("trial_success_refunded_heading", "Slot no longer available")}</h1>
          <p className="text-[13px] text-[#676664]/70 leading-relaxed mb-3">
            {t("trial_success_refunded_message", "Someone booked that slot just before your payment cleared. We've refunded your payment in full.")}
            {state.amount ? ` (${`$${Number(state.amount).toFixed(2)}`})` : ""}
          </p>
          <p className="text-[13px] text-[#676664]/60 mb-6">Please choose another time — we&apos;d love to see you.</p>
          <Link to="/booking/trial" className="inline-block text-[11px] tracking-[0.14em] uppercase text-[#9A7B4F] border-b border-[#9A7B4F]/40 pb-1 hover:text-[#8B6B3F]" data-testid="rebook-link">
            {t("trial_success_refunded_cta", "Choose Another Trial Time")}
          </Link>
        </div>
      </div>
    );
  }

  if (state.status === "confirmed" && details) {
    return (
      <div className={containerCls} data-testid="success-confirmed">
        <div className={cardCls}>
          <div className="w-16 h-16 mx-auto mb-6 bg-[#B8C9BD]/20 flex items-center justify-center">
            <Check className="w-7 h-7 text-[#6B8F71]" strokeWidth={1.5} />
          </div>
          <h1 className={headingCls} style={headingStyle}>{t("trial_success_heading", "Trial Booked")}</h1>
          <p className="text-[14px] text-[#676664]/70 mb-2 leading-relaxed">{t("trial_success_subheading", "Your trial is confirmed and paid.")}</p>
          <p className="text-[15px] text-[#9A7B4F] font-medium mb-3" data-testid="confirmed-datetime">
            {new Date(details.date + "T00:00:00").toLocaleDateString("en-AU", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}<br />
            {details.start_time} · {details.duration_minutes} minutes (AEST)
          </p>
          <p className="text-[11px] text-[#676664]/50 mb-6">
            Paid ${Number(details.amount_paid || 0).toFixed(2)} {(details.currency || "aud").toUpperCase()} · Confirmation sent to {details.customer_email}
          </p>
          <TrialCalendarOptions
            details={{
              date: details.date,
              startTime: details.start_time,
              durationMinutes: details.duration_minutes,
              customerName: details.customer_name,
              customerEmail: details.customer_email,
            }}
          />
          <Link to="/" className="block text-[11px] tracking-[0.14em] uppercase text-[#282828]/60 border-b border-[#282828]/20 pb-1 hover:text-[#9A7B4F] hover:border-[#9A7B4F]">
            {t("trial_success_continue_cta", "Continue Browsing")}
          </Link>
        </div>
      </div>
    );
  }

  // timeout or error
  return (
    <div className={containerCls} data-testid="success-error">
      <div className={cardCls}>
        <h1 className={headingCls} style={headingStyle}>{t("trial_success_error_heading", "We'll be in touch")}</h1>
        <p className="text-[13px] text-[#676664]/70 leading-relaxed mb-6">
          {t("trial_success_error_message", "Your payment succeeded but our confirmation is still catching up. You'll receive an email shortly. If you don't hear from us within 15 minutes, please contact us with your payment reference.")}
        </p>
        <Link to="/" className="inline-block text-[11px] tracking-[0.14em] uppercase text-[#9A7B4F] border-b border-[#9A7B4F]/40 pb-1 hover:text-[#8B6B3F]">
          Back to Home
        </Link>
      </div>
    </div>
  );
}
